import { useState, useEffect, useRef } from "react";
import { inventoryTransactionApi, inventorySummaryApi, productApi, inventoryItemDtlApi, vendorApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination }       from "../hooks/usePagination";
import Pagination              from "../components/Pagination";
import SearchableDropdown      from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ── Transaction type config ── */
const TRANSACTION_TYPES = [
  { value: "Stock In",   label: "Stock In",   color: "in"  },
  { value: "Stock Out",  label: "Stock Out",  color: "out" },
  { value: "Adjustment", label: "Adjustment", color: "adj" },
  { value: "Damage",     label: "Damage",     color: "dmg" },
];

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Transaction?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Transaction Detail Drawer ── */
function TransactionDrawer({ txn, getProductName, getVendor, getStockLabel, getItemLabel, onClose, onEdit }) {
  const typeConf = TRANSACTION_TYPES.find(t => t.value === txn.transactionType) ?? {};
  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-txn-drawer" onClick={e => e.stopPropagation()}>
        <div className="rv-txn-drawer-header">
          <div className={`rv-txn-drawer-avatar rv-txn-avatar--${typeConf.color ?? "undefined"}`}>
            <TxnIcon type={typeConf.color} />
          </div>
          <div className="rv-txn-drawer-title-wrap">
            <h3 className="rv-txn-drawer-name">Transaction #{txn.transactionID}</h3>
            <span className={`rv-txn-type-pill rv-txn-type-pill--${typeConf.color ?? "undefined"}`}>
              {txn.transactionType || "—"}
            </span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-txn-drawer-body">
          <div className="rv-txn-detail-grid">
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><ProductIcon /> Product</span>
              <span className="rv-txn-detail-val">{getProductName(txn.productID)}</span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><TruckIcon /> Vendor / Supplier</span>
              <span className="rv-txn-detail-val">
                {(() => {
                  const v = getVendor ? getVendor(txn.productID) : null;
                  if (!v) return <span className="rv-empty-val">—</span>;
                  return (
                    <span className="rv-txn-vendor-chip">
                      <strong>{v.vendorName}</strong>
                      {(v.phoneNumber || v.contactNumber) ? (
                        <span className="rv-txn-vendor-sub">{v.phoneNumber || v.contactNumber}</span>
                      ) : null}
                    </span>
                  );
                })()}
              </span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><QtyIcon /> Quantity</span>
              <span className="rv-txn-detail-val rv-txn-detail-qty">{txn.quantity ?? "—"}</span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><ItemIcon /> Inventory Item</span>
              <span className="rv-txn-detail-val">{getItemLabel(txn.inventoryItemID)}</span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><StockIcon /> Stock Entry</span>
              <span className="rv-txn-detail-val">{getStockLabel(txn.stockID)}</span>
            </div>
            {txn.comments && (
              <div className="rv-txn-detail-item rv-txn-detail-item--full">
                <span className="rv-txn-detail-label"><CommentIcon /> Comments</span>
                <span className="rv-txn-detail-val rv-txn-detail-comment">{txn.comments}</span>
              </div>
            )}
            {txn.lastUPDTTM && (
              <div className="rv-txn-detail-item rv-txn-detail-item--full">
                <span className="rv-txn-detail-label"><ClockIcon /> Last Updated</span>
                <span className="rv-txn-detail-val">
                  {new Date(txn.lastUPDTTM).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="rv-txn-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onEdit(txn); onClose(); }}>
            <EditIcon /> Edit Transaction
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Transaction Form Modal ── */
function TransactionModal({ initial, onSave, onClose, saving, products, stockEntries, inventoryItems, vendors = [] }) {
  const isEdit = !!initial?.transactionID;

  // Determine initial vendor ID from initial product if editing
  const initialProd = products.find(p => p.productID === Number(initial?.productID));
  const initialVendorID = initialProd?.vendorID ? String(initialProd.vendorID) : "";

  const [selectedVendorID, setSelectedVendorID] = useState(initialVendorID);
  const [form, setForm] = useState({
    inventoryItemID: initial?.inventoryItemID ?? "",
    stockID:         initial?.stockID         ?? "",
    productID:       initial?.productID       ?? "",
    transactionType: initial?.transactionType ?? "",
    quantity:        initial?.quantity        ?? "",
    comments:        initial?.comments        ?? "",
  });
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  /* When vendor is selected, filter available products; if current product doesn't belong to vendor, clear it */
  const handleVendorSelect = (val) => {
    const vID = val ? String(val) : "";
    setSelectedVendorID(vID);
    if (vID) {
      const currentProd = products.find(p => p.productID === Number(form.productID));
      if (currentProd && String(currentProd.vendorID) !== vID) {
        set("productID", "");
      }
    }
  };

  /* When product is selected, auto-sync vendor */
  const handleProductSelect = (val) => {
    const pID = Number(val) || "";
    set("productID", pID);
    if (pID) {
      const prod = products.find(p => p.productID === pID);
      if (prod?.vendorID) {
        setSelectedVendorID(String(prod.vendorID));
      }
    }
  };

  /* Filtered product options based on selected vendor */
  const availableProducts = selectedVendorID
    ? products.filter(p => String(p.vendorID) === String(selectedVendorID))
    : products;

  /* When inventory item is selected, auto-fill product, vendor & stock if available */
  const handleItemSelect = (itemID) => {
    set("inventoryItemID", Number(itemID) || "");
    if (itemID) {
      const item = inventoryItems.find(i => i.inventoryItemID === Number(itemID));
      if (item && item.productID) {
        handleProductSelect(item.productID);
        setForm(f => ({
          ...f,
          inventoryItemID: Number(itemID),
          productID: item.productID,
          stockID: item.stockID || f.stockID
        }));
      }
    }
  };

  const validate = () => {
    const e = {};
    if (!form.productID)       e.productID       = "Product is required.";
    if (!form.transactionType) e.transactionType = "Transaction type is required.";
    if (form.quantity === "" || form.quantity === null)
                               e.quantity        = "Quantity is required.";
    if (Number(form.quantity) < 0) e.quantity    = "Quantity cannot be negative.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    // Preserve and append vendor context into comments for traceability
    let finalComments = form.comments || "";
    const activeVendor = vendors.find(v => String(v.vendorID) === String(selectedVendorID));
    if (activeVendor && !finalComments.toLowerCase().includes(activeVendor.vendorName.toLowerCase())) {
      finalComments = finalComments ? `[Supplier: ${activeVendor.vendorName}] ${finalComments}` : `[Supplier: ${activeVendor.vendorName}]`;
    }

    onSave({ ...initial, ...form, comments: finalComments });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-txn-modal">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><TxnIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Transaction" : "New Transaction"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the transaction details." : "Record a new inventory movement."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">

          {/* Row 1: Transaction Type + Quantity — 2 col */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Transaction Type <span className="rv-required">*</span></label>
              <SearchableDropdown
                options={TRANSACTION_TYPES}
                valueKey="value"
                labelKey="label"
                value={form.transactionType}
                onChange={val => set("transactionType", val)}
                placeholder="Select type…"
                searchable={false}
                error={errors.transactionType}
                pageSize={10}
              />
            </div>

            <div className="rv-field">
              <label className="rv-label">Quantity <span className="rv-required">*</span></label>
              <input
                type="number"
                min="0"
                className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`}
                placeholder="e.g. 10"
                value={form.quantity}
                onChange={e => set("quantity", e.target.value)}
              />
              {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
            </div>
          </div>

          {/* Row 2: Vendor / Supplier + Product — 2 col */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Vendor / Supplier</label>
              <SearchableDropdown
                options={[
                  { vendorID: "", vendorName: "All Vendors" },
                  ...vendors.map(v => ({ vendorID: String(v.vendorID), vendorName: v.vendorName }))
                ]}
                valueKey="vendorID"
                labelKey="vendorName"
                value={selectedVendorID}
                onChange={val => handleVendorSelect(val)}
                placeholder="Select vendor…"
                searchPlaceholder="Search vendors…"
                pageSize={8}
              />
            </div>

            <div className="rv-field">
              <label className="rv-label">Product <span className="rv-required">*</span></label>
              <SearchableDropdown
                options={availableProducts}
                valueKey="productID"
                labelKey="productName"
                value={form.productID}
                onChange={val => handleProductSelect(val)}
                placeholder={selectedVendorID ? "Select product for vendor…" : "Select product…"}
                searchPlaceholder="Search products…"
                error={errors.productID}
                pageSize={8}
              />
            </div>
          </div>

          {/* Inventory Item */}
          <div className="rv-field">
            <label className="rv-label">Inventory Item</label>
            <SearchableDropdown
              options={[
                { inventoryItemID: "", label: "Select inventory item…" },
                ...inventoryItems.map(item => ({
                  inventoryItemID: item.inventoryItemID,
                  label: `Item #${item.inventoryItemID}${item.productID ? ` — Prod #${item.productID}` : ""}${item.quantity != null ? ` (Qty: ${item.quantity})` : ""}`
                }))
              ]}
              valueKey="inventoryItemID"
              labelKey="label"
              value={form.inventoryItemID}
              onChange={val => handleItemSelect(val)}
              placeholder="Select inventory item…"
              searchPlaceholder="Search inventory items…"
              pageSize={10}
            />
            <p className="rv-field-hint">Selecting an item auto-fills Product, Vendor & Stock.</p>
          </div>

          {/* Stock Entry */}
          <div className="rv-field">
            <label className="rv-label">Stock Entry</label>
            <SearchableDropdown
              options={[
                { stockID: "", label: "Select stock entry…" },
                ...stockEntries.map(s => ({
                  stockID: s.stockID,
                  label: `Stock #${s.stockID}${s.productID ? ` — Prod #${s.productID}` : ""}${s.quantity != null ? ` (Qty: ${s.quantity})` : ""}`
                }))
              ]}
              valueKey="stockID"
              labelKey="label"
              value={form.stockID}
              onChange={val => set("stockID", val ? Number(val) : "")}
              placeholder="Select stock entry…"
              searchPlaceholder="Search stock entries…"
              pageSize={10}
            />
          </div>

          {/* Comments */}
          <div className="rv-field">
            <label className="rv-label">Comments</label>
            <textarea
              className="rv-input rv-textarea"
              placeholder="Optional notes about this transaction…"
              rows={3}
              value={form.comments}
              onChange={e => set("comments", e.target.value)}
            />
          </div>

        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Record"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Operational Hub Nav ── */
function HubNav({ active = "Inventory Transaction" }) {
  const navTo = (target) => {
    window.dispatchEvent(new CustomEvent("reva:navigate", { detail: target }));
  };

  return (
    <div className="rv-hub-nav">
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stock Receipt" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stock Receipt")}
      >
        <ReceiptIcon /> Vendor Inward (Stock Receipts)
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stocks" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stocks")}
      >
        <TagIcon /> Jewelry Items & Barcodes
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Bulk Stock In" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Bulk Stock In")}
      >
        <BoxIcon /> Stock Balances
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Inventory Transaction" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Inventory Transaction")}
      >
        <HubLedgerIcon /> Movement Ledger
      </button>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN INVENTORY TRANSACTION PAGE
══════════════════════════════════════ */
export default function InventoryTransactionPage() {
  const [transactions,   setTransactions]   = useState([]);
  const [products,       setProducts]       = useState([]);
  const [vendors,        setVendors]        = useState([]);
  const [stockEntries,   setStockEntries]   = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading,        setLoading]        = useState(true);

  const [search,       setSearch]       = useState("");
  const [filterType,   setFilterType]   = useState("all");
  const [filterProd,   setFilterProd]   = useState("all");
  const [filterVendor, setFilterVendor] = useState("all");

  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer,  setDrawer]  = useState(null);
  const [toast,   setToast]   = useState(null);

  /* ── Resizable columns ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 110, 180, 160, 100, 100, 80, 160, 130, 90]);

  /* ── Fetch all ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [txnData, prodData, stockData, itemData, vendorData] = await Promise.all([
        inventoryTransactionApi.getAll(),
        productApi.getAll(),
        inventorySummaryApi.getAll(),
        inventoryItemDtlApi.getAll(),
        vendorApi.getAll(),
      ]);
      setTransactions( Array.isArray(txnData)   ? txnData   : txnData?.data   ?? []);
      setProducts(     Array.isArray(prodData)   ? prodData  : prodData?.data  ?? []);
      setStockEntries( Array.isArray(stockData)  ? stockData : stockData?.data ?? []);
      setInventoryItems(Array.isArray(itemData)  ? itemData  : itemData?.data  ?? []);
      setVendors(       Array.isArray(vendorData) ? vendorData : vendorData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Lookup helpers ── */
  const getProductName = id => products.find(p => p.productID === id)?.productName ?? (id ? `Prod #${id}` : "—");
  const getVendor      = prodId => {
    const prod = products.find(p => p.productID === prodId);
    if (!prod || !prod.vendorID) return null;
    return vendors.find(v => v.vendorID === prod.vendorID) ?? null;
  };
  const getVendorName  = prodId => getVendor(prodId)?.vendorName ?? "—";
  const getStockLabel  = id => id ? `Stock #${id}` : "—";
  const getItemLabel   = id => id ? `Item #${id}` : "—";

  /* ── Stats ── */
  const statCounts = TRANSACTION_TYPES.reduce((acc, t) => {
    acc[t.value] = transactions.filter(tx => tx.transactionType === t.value).length;
    return acc;
  }, {});

  /* ── Product & Vendor options for filter ── */
  const prodOptions = Array.from(
    new Map(transactions.map(tx => [tx.productID, getProductName(tx.productID)])).entries()
  );
  const vendorOptions = vendors.filter(v => v && v.vendorID);

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.transactionID) {
        await inventoryTransactionApi.update(formData);
        showToast("Transaction updated successfully.");
      } else {
        await inventoryTransactionApi.create(formData);
        showToast("Transaction recorded successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await inventoryTransactionApi.remove(confirm.id);
      showToast("Transaction deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = transactions.filter(tx => {
    const q = search.toLowerCase();
    const vName = getVendorName(tx.productID).toLowerCase();
    const matchSearch =
      !q ||
      getProductName(tx.productID).toLowerCase().includes(q) ||
      vName.includes(q) ||
      tx.transactionType?.toLowerCase().includes(q) ||
      tx.comments?.toLowerCase().includes(q) ||
      String(tx.transactionID).includes(q) ||
      String(tx.inventoryItemID).includes(q);
    const matchType   = filterType === "all" || tx.transactionType === filterType;
    const matchProd   = filterProd === "all" || tx.productID === Number(filterProd);
    const matchVendor = filterVendor === "all" || getVendor(tx.productID)?.vendorID === Number(filterVendor);
    return matchSearch && matchType && matchProd && matchVendor;
  });

  /* ── Pagination ── */
  const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);

  /* ── Format date ── */
  const fmtDate = iso => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="rv-txn-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={`Transaction #${confirm.id}`}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <TransactionModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          products={products}
          stockEntries={stockEntries}
          inventoryItems={inventoryItems}
          vendors={vendors}
        />
      )}
      {drawer && (
        <TransactionDrawer
          txn={drawer}
          getProductName={getProductName}
          getVendor={getVendor}
          getStockLabel={getStockLabel}
          getItemLabel={getItemLabel}
          onClose={() => setDrawer(null)}
          onEdit={tx => setModal({ data: tx })}
        />
      )}

      <HubNav active="Inventory Transaction" />

      {/* ── Topbar ── */}
      <div className="rv-txn-topbar">
        <div className="rv-txn-topbar-left">
          <h2 className="rv-txn-heading">Inventory Transactions</h2>
          <span className="rv-txn-count">{transactions.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Transaction
        </button>
      </div>

      {/* ── Stat pills ── */}
      {!loading && transactions.length > 0 && (
        <div className="rv-txn-stats">
          {TRANSACTION_TYPES.map(t => (
            <div key={t.value} className="rv-txn-stat-pill">
              <div className={`rv-txn-stat-icon rv-txn-stat-icon--${t.color}`}>
                <TxnIcon type={t.color} />
              </div>
              <div>
                <p className="rv-txn-stat-label">{t.label}</p>
                <p className="rv-txn-stat-val">{statCounts[t.value] ?? 0}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="rv-txn-filters">
        <div className="rv-txn-search">
          <SearchIcon />
          <input
            placeholder="Search by product, type, item ID, comments…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-txn-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <SearchableDropdown
          options={[
            { id: "all", name: "All Products" },
            ...prodOptions.map(([id, name]) => ({ id, name }))
          ]}
          valueKey="id"
          labelKey="name"
          value={filterProd}
          onChange={val => { setFilterProd(val); setPage(1); }}
          placeholder="All Products"
          searchPlaceholder="Search products…"
          className="rv-txn-prod-filter"
          pageSize={10}
        />

        <SearchableDropdown
          options={[
            { id: "all", name: "All Vendors" },
            ...vendorOptions.map(v => ({ id: v.vendorID, name: v.vendorName }))
          ]}
          valueKey="id"
          labelKey="name"
          value={filterVendor}
          onChange={val => { setFilterVendor(val); setPage(1); }}
          placeholder="All Vendors"
          searchPlaceholder="Search vendors…"
          className="rv-txn-vendor-filter"
          pageSize={10}
        />

        <div className="rv-filter-tabs">
          <button
            className={`rv-filter-tab ${filterType === "all" ? "rv-filter-tab--active" : ""}`}
            onClick={() => { setFilterType("all"); setPage(1); }}
          >All</button>
          {TRANSACTION_TYPES.map(t => (
            <button
              key={t.value}
              className={`rv-filter-tab rv-filter-tab--txn ${filterType === t.value ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterType(t.value); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-txn-card">
        {/* Desktop Table View */}
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-txn-empty">
              <div className="rv-loader" />
              <p>Loading transactions…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rv-txn-empty">
              <div className="rv-empty-icon"><TxnIcon /></div>
              <p className="rv-empty-title">No transactions found</p>
              <p className="rv-empty-sub">
                {search ? "Try a different search term." : "Record your first inventory transaction."}
              </p>
              {!search && (
                <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Transaction
                </button>
              )}
            </div>
          ) : (
            <div className="rv-table-wrap rv-txn-table-wrap">
              <table className="rv-table rv-txn-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Product</th>
                    <th>Vendor</th>
                    <th>Item ID</th>
                    <th>Stock ID</th>
                    <th>Qty</th>
                    <th>Comments</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((tx, i) => {
                    const typeConf = TRANSACTION_TYPES.find(t => t.value === tx.transactionType) ?? {};
                    return (
                      <tr
                        key={tx.transactionID}
                        className="rv-txn-row"
                        onClick={() => setDrawer(tx)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="rv-txn-idx">{pageStart + i}</td>

                        {/* Type */}
                        <td>
                          <span className={`rv-txn-type-pill rv-txn-type-pill--${typeConf.color ?? "undefined"}`}>
                            {tx.transactionType || "—"}
                          </span>
                        </td>

                        {/* Product */}
                        <td>
                          <div className="rv-txn-prod-cell">
                            <span className="rv-txn-prod-name">{getProductName(tx.productID)}</span>
                          </div>
                        </td>

                        {/* Vendor */}
                        <td>
                          {getVendor(tx.productID) ? (
                            <span className="rv-badge-vendor" title={getVendorName(tx.productID)}>
                              <TruckIcon />
                              <span>{getVendorName(tx.productID)}</span>
                            </span>
                          ) : (
                            <span className="rv-empty-val">—</span>
                          )}
                        </td>

                        {/* Inventory Item ID */}
                        <td>
                          {tx.inventoryItemID
                            ? <span className="rv-txn-id-badge">#{tx.inventoryItemID}</span>
                            : <span className="rv-empty-val">—</span>}
                        </td>

                        {/* Stock ID */}
                        <td>
                          {tx.stockID
                            ? <span className="rv-txn-id-badge">#{tx.stockID}</span>
                            : <span className="rv-empty-val">—</span>}
                        </td>

                        {/* Qty */}
                        <td>
                          <span className={`rv-txn-qty rv-txn-qty--${typeConf.color ?? "undefined"}`}>
                            {typeConf.color === "out" || typeConf.color === "dmg" ? "−" : "+"}
                            {tx.quantity ?? 0}
                          </span>
                        </td>

                        {/* Comments */}
                        <td>
                          <span className="rv-txn-comment-cell">
                            {tx.comments || <span className="rv-empty-val">—</span>}
                          </span>
                        </td>

                        {/* Date */}
                        <td>
                          <span className="rv-txn-date">{fmtDate(tx.lastUPDTTM)}</span>
                        </td>

                        {/* Actions */}
                        <td onClick={e => e.stopPropagation()}>
                          <div className="rv-actions">
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => setModal({ data: tx })}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => setConfirm({ id: tx.transactionID })}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={paged}
            emptyIcon={<TxnIcon />}
            emptyTitle="No transactions found"
            emptySubtitle={search ? "Try a different search term." : "Record your first inventory transaction."}
            emptyAction={
              !search && (
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Transaction
                </button>
              )
            }
            renderCard={(tx, i) => {
              const typeConf = TRANSACTION_TYPES.find(t => t.value === tx.transactionType) ?? {};
              const qtyPrefix = typeConf.color === "out" || typeConf.color === "dmg" ? "−" : "+";
              return (
                <RvDataCard
                  key={tx.transactionID}
                  index={pageStart + i}
                  title={getProductName(tx.productID)}
                  subtitle={`Txn #${tx.transactionID} • Qty: ${qtyPrefix}${tx.quantity ?? 0}`}
                  badge={
                    <span className={`rv-txn-type-pill rv-txn-type-pill--${typeConf.color ?? "undefined"}`}>
                      {tx.transactionType || "—"}
                    </span>
                  }
                  fields={[
                    { label: "Vendor", value: getVendorName(tx.productID) },
                    { label: "Quantity", value: `${qtyPrefix}${tx.quantity ?? 0}` },
                    { label: "Date", value: fmtDate(tx.lastUPDTTM) },
                    { label: "Item ID", value: tx.inventoryItemID ? `#${tx.inventoryItemID}` : "—" },
                    { label: "Stock ID", value: tx.stockID ? `#${tx.stockID}` : "—" },
                    ...(tx.comments ? [{ label: "Comments", value: tx.comments, fullWidth: true }] : []),
                  ]}
                  onClick={() => setDrawer(tx)}
                  actions={
                    <div className="rv-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="rv-action-btn rv-action-btn--edit"
                        title="Edit"
                        onClick={() => setModal({ data: tx })}
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="rv-action-btn rv-action-btn--del"
                        title="Delete"
                        onClick={() => setConfirm({ id: tx.transactionID })}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  }
                />
              );
            }}
          />
        </div>

        {/* Shared Pagination for Desktop & Mobile */}
        {!loading && filtered.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
            pageStart={pageStart}
            pageEnd={pageEnd}
            totalItems={totalItems}
          />
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view">
          <GripIcon /> Drag column borders to resize · Click a row to view details
        </p>
      )}
    </div>
  );
}

/* ── TxnIcon — changes per type ── */
function TxnIcon({ type }) {
  if (type === "in")  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>;
  if (type === "out") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>;
  if (type === "adj") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;
  if (type === "dmg") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><polyline points="8 21 12 17 16 21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
}

function PlusIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>; }
function GripIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }
function ProductIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function QtyIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function StockIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M5 8h14M5 8a2 2 0 1 0-4 0 2 2 0 0 0 4 0zM5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-14 0a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2"/></svg>; }
function ItemIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function CommentIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>; }
function ClockIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }

function ReceiptIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>; }
function TagIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function BoxIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>; }
function HubLedgerIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M7 16V4m0 0L3 8m4-4l4 4m6 4v12m0 0l4-4m-4 4l-4-4"/></svg>; }
function TruckIcon({ size = 14 }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width={size} height={size} style={{ width: size, height: size, flexShrink: 0 }}><rect x="1" y="3" width="15" height="13" rx="1"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>; }
